import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ctoken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Send current UI language to backend for email template selection
  const lang = localStorage.getItem('lockerhq_language') || 'ka';
  config.headers['Accept-Language'] = lang;
  return config;
});

// Pages anyone may use without logging in (a recipient opening the SMS link,
// returning from the payment page, the welcome screen).
const PUBLIC_PATHS = ['/', '/welcome', '/pickup', '/payment-result', '/login', '/delivery'];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Don't redirect on login/register/SMS endpoints — let the page handle errors
      const url = err.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login')
        || url.includes('/auth/register')
        || url.includes('/auth/sms-login')
        || url.includes('/auth/send-phone-code')
        || url.includes('/auth/verify-phone-code')
        || url.includes('/auth/forgot-password')
        || url.includes('/auth/reset-password');

      if (!isAuthEndpoint) {
        // The saved login expired: forget it. Only send the user to the login
        // page from pages that need a login, and bring them back afterwards.
        localStorage.removeItem('ctoken');
        localStorage.removeItem('cuser');
        const here = window.location.pathname;
        if (!PUBLIC_PATHS.includes(here)) {
          window.location.href = '/login?redirect=' + encodeURIComponent(here + window.location.search);
        }
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
  sendPhoneCode: (phone) => api.post('/auth/send-phone-code', { phone }),
  verifyPhoneCode: (phone, code) => api.post('/auth/verify-phone-code', { phone, code }),
  smsLoginSend: (phone) => api.post('/auth/sms-login/send', { phone }),
  smsLoginVerify: (phone, code) => api.post('/auth/sms-login/verify', { phone, code }),
};

export const paymentAPI = {
  calculate: (pickup_code) => api.post('/payments/calculate', { pickup_code }),
  create: (pickup_code) => api.post('/payments/create', { pickup_code }),
  pickupStatus: (pickup_code) => api.post('/payments/pickup-status', { pickup_code }),
  openPaid: (pickup_code) => api.post('/payments/open-paid', { pickup_code }),
};

export const customerAPI = {
  pricing: () => api.get('/customer/pricing'),
  myParcels: () => api.get('/customer/my-parcels'),
  locations: () => api.get('/customer/locations'),
  send: (data) => api.post('/customer/send', data),
  store: (save_code, device_number, box_size) =>
    api.post('/customer/store', null, { params: { save_code, device_number, box_size } }),
  myOrders: () => api.get('/customer/my-orders'),
  checkDoorClosed: (order_no) => api.post('/customer/check-door-closed', { order_no }),
  cancelDraft: (order_no) => api.post('/customer/cancel-draft', { order_no }),
};

export default api;
